// Subscription Service for Razorpay Subscriptions using Firebase Functions
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/config';

class SubscriptionService {
  // Create a Razorpay Plan using Firebase Function
  async createPlan({ amount, period, interval, name, description, notes = {} }) {
    try {
      const createPlanFunction = httpsCallable(functions, 'createRazorpayPlan');
      const result = await createPlanFunction({
        amount,
        period,
        interval,
        name,
        description,
        notes
      });
      
      return result.data;
    } catch (error) {
      return { 
        success: false, 
        error: error.message || 'Failed to create plan. Please check Firebase Functions configuration.' 
      };
    }
  }

  // Create a Razorpay Subscription using Firebase Function
  async createSubscription({ planId, customerNotify = 1, totalCount, notes = {}, customerDetails }) {
    try {
      const createSubscriptionFunction = httpsCallable(functions, 'createRazorpaySubscription');
      const result = await createSubscriptionFunction({
        planId,
        customerNotify,
        totalCount,
        notes,
        customerDetails
      });
      
      return result.data;
    } catch (error) {
      console.error('Subscription creation error:', error);
      // Extract more detailed error information
      const errorMessage = error.message || 'Failed to create subscription';
      const errorDetails = error.details || errorMessage;
      
      return { 
        success: false, 
        error: errorDetails || 'Failed to create subscription. Please check Firebase Functions configuration and Razorpay credentials.' 
      };
    }
  }

  // Cancel a Razorpay Subscription using Firebase Function
  async cancelSubscription(subscriptionId, cancelAtCycleEnd = false) {
    try {
      const cancelSubscriptionFunction = httpsCallable(functions, 'cancelRazorpaySubscription');
      const result = await cancelSubscriptionFunction({
        subscriptionId,
        cancelAtCycleEnd
      });
      
      return result.data;
    } catch (error) {
      return { 
        success: false, 
        error: error.message || 'Failed to cancel subscription. Please check Firebase Functions configuration.' 
      };
    }
  }

  // Get Subscription details using Firebase Function
  async getSubscription(subscriptionId) {
    try {
      const getSubscriptionFunction = httpsCallable(functions, 'getRazorpaySubscription');
      const result = await getSubscriptionFunction({ subscriptionId });
      
      return result.data;
    } catch (error) {
      return { 
        success: false, 
        error: error.message || 'Failed to get subscription. Please check Firebase Functions configuration.' 
      };
    }
  }

  // Get or create plan for a subscription type
  async getOrCreatePlan(planType, amount) {
    try {
      // Map plan types to Razorpay periods
      const planConfig = {
        monthly: { period: 'monthly', interval: 1, name: 'Monthly Coaching Subscription' },
        quarterly: { period: 'monthly', interval: 3, name: 'Quarterly Coaching Subscription' },
        yearly: { period: 'monthly', interval: 12, name: 'Yearly Coaching Subscription' }
      };

      const config = planConfig[planType];
      if (!config) {
        return { success: false, error: 'Invalid plan type' };
      }

      // Create plan with unique identifier in notes
      const planId = `plan_${planType}_${Math.round(amount)}`;
      const result = await this.createPlan({
        amount,
        period: config.period,
        interval: config.interval,
        name: config.name,
        description: `Auto-renewing ${planType} subscription for coaching`,
        notes: {
          planType,
          planId,
          coachingSubscription: true
        }
      });

      return result;
    } catch (error) {
      return { success: false, error: error.message };
    }
  }
}

// Create singleton instance
const subscriptionService = new SubscriptionService();

export default subscriptionService;

